import { useTranslation } from 'react-i18next'
import { formatDisplayNumber } from '../../lib/format'
import type { ThematicLegendData } from './api'
import { NOT_APPLICABLE, NOT_APPLICABLE_COLOUR, UNMATCHED, UNSET, UNSET_COLOUR, pickLabel } from './colour'

interface ThematicLegendProps {
  legend: ThematicLegendData
  /** The class value currently isolated, or null. */
  isolated: string | null
  onToggleIsolated: (value: string) => void
  /** Link to the screen that edits the colours (users with configuration write). */
  editColoursHref?: string
  /** Link to the editor of the definition behind the legend, for a stored thematic. */
  editDefinitionHref?: string
  /** Unit of the surface column. */
  surfaceUnit?: string
}

function Row({
  testId, value, colour, label, count, surface, isolated, onToggle, surfaceUnit,
}: {
  testId: string
  value: string
  colour: string
  label: string
  count: number
  surface: number
  isolated: string | null
  onToggle: (value: string) => void
  surfaceUnit: string
}) {
  const active = isolated === value
  return (
    <button
      type="button"
      data-testid={testId}
      data-class-value={value}
      aria-pressed={active}
      onClick={() => onToggle(value)}
      className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs transition-colors ${
        active ? 'border-border bg-accent' : 'border-transparent hover:bg-accent/60'
      } ${isolated && !active ? 'opacity-50' : ''}`}
    >
      <span
        className="inline-block h-3 w-3 shrink-0 rounded-sm"
        style={{ background: colour }}
        data-testid={`${testId}-swatch`}
      />
      <span className="text-foreground">{label}</span>
      <span className="font-semibold text-foreground" data-testid={`${testId}-count`}>{count}</span>
      {surface > 0 && (
        <span className="text-muted-foreground" data-testid={`${testId}-surface`}>
          {formatDisplayNumber(surface)} {surfaceUnit}
        </span>
      )}
    </button>
  )
}

/**
 * The legend of a thematic: one row per class that has elements, then the
 * "unset" and "not applicable" rows. A row isolates its class and dims the
 * others; a second click lifts the isolation (spec 2026-10-06-10).
 */
export function ThematicLegend({ legend, isolated, onToggleIsolated, editColoursHref, editDefinitionHref, surfaceUnit = 'm²' }: ThematicLegendProps) {
  const { t, i18n } = useTranslation('thematics')
  const lang = i18n.language || 'en'
  const classes = (legend.classes ?? []).filter((c) => (c.count ?? 0) > 0)
  const unset = legend.unset
  const notApplicable = legend.not_applicable
  const unmatched = legend.unmatched

  return (
    <div data-testid="thematic-legend" className="flex flex-wrap items-center gap-1">
      {classes.map((c) => (
        <Row
          key={c.value}
          testId={`thematic-legend-row-${c.value}`}
          value={c.value ?? ''}
          colour={c.colour ?? UNSET_COLOUR}
          label={pickLabel(c.label, lang, c.value)}
          count={c.count ?? 0}
          surface={c.surface ?? 0}
          isolated={isolated}
          onToggle={onToggleIsolated}
          surfaceUnit={surfaceUnit}
        />
      ))}
      {(unset?.count ?? 0) > 0 && (
        <Row
          testId="thematic-legend-unset"
          value={UNSET}
          colour={UNSET_COLOUR}
          label={t('unset')}
          count={unset?.count ?? 0}
          surface={unset?.surface ?? 0}
          isolated={isolated}
          onToggle={onToggleIsolated}
          surfaceUnit={surfaceUnit}
        />
      )}
      {(notApplicable?.count ?? 0) > 0 && (
        <Row
          testId="thematic-legend-not-applicable"
          value={NOT_APPLICABLE}
          colour={NOT_APPLICABLE_COLOUR}
          label={t('not_applicable')}
          count={notApplicable?.count ?? 0}
          surface={notApplicable?.surface ?? 0}
          isolated={isolated}
          onToggle={onToggleIsolated}
          surfaceUnit={surfaceUnit}
        />
      )}
      {(unmatched?.count ?? 0) > 0 && (
        <Row
          testId="thematic-legend-unmatched"
          value={UNMATCHED}
          colour={NOT_APPLICABLE_COLOUR}
          label={t('unmatched')}
          count={unmatched?.count ?? 0}
          surface={unmatched?.surface ?? 0}
          isolated={isolated}
          onToggle={onToggleIsolated}
          surfaceUnit={surfaceUnit}
        />
      )}
      {editDefinitionHref && (
        <a
          href={editDefinitionHref}
          data-testid="thematic-legend-edit-definition"
          className="ml-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {t('edit_definition')}
        </a>
      )}
      {editColoursHref && (
        <a
          href={editColoursHref}
          data-testid="thematic-legend-edit-colours"
          className="ml-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {t('edit_colours')}
        </a>
      )}
    </div>
  )
}
